// src/features/license/format.ts
// Pure helpers shared across the license feature — no React imports.

import type { Entity } from '@core/types/entity';

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

/** Strip an ENUM_PREFIX_ off a proto enum value and lowercase it for i18n keys. */
export function enumKey(value: string | undefined, prefix: string): string {
  if (!value) return 'unspecified';
  return value.startsWith(prefix) ? value.slice(prefix.length).toLowerCase() : value.toLowerCase();
}

export function fmtDate(value: string | undefined, locale = 'en'): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
}

export function fmtDateTime(value: string | undefined, locale = 'en'): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(locale, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return value;
  }
}

/** Days between now and an ISO timestamp — negative if in the past. */
export function daysUntil(value: string | undefined): number | null {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  return Math.ceil((target - Date.now()) / (24 * 60 * 60 * 1000));
}

export function bytesToHuman(bytes: number): string {
  if (!bytes) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/** A crude hardware fingerprint for this browser/session — the real signal
 * comes from the local agent on an actual on-prem host; this is enough to
 * populate the activation form so the request is well-formed. */
export function currentHardwareFingerprint() {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  return {
    fingerprint_hash: '',
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    machine_id: '',
    mac_addresses: [] as string[],
    cpu_id: '',
    cpu_cores: nav?.hardwareConcurrency ?? 0,
    memory_bytes: 0,
    os_type: nav?.platform ?? '',
    cloud_instance_id: '',
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function listParams(pagination: { cursor?: string; limit?: number }, filters: Record<string, any> = {}): any {
  return {
    pagination: { cursor: pagination.cursor ?? '', limit: pagination.limit ?? 50, direction: 'forward' },
    ...filters,
  };
}
